import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styles from './index.module.scss'
import cn from 'classnames'
import DatePicker from 'react-datepicker'
import { enumToTitleCase } from '../../../utilities/stringUtilities'
import { INVOICE_STATUSES } from '../../../utilities/constants'
import dayjs from '../../../utilities/dayjs'
import { Spinner } from '../../../pages/shared/Spinner'
import Autocomplete from '../../autoComplete/AutoComplete'
export default function
InvoicesSearchForm ({
  onSubmit,
  onClear,
  isFetching = false,
  isWastepayEnabled,
  isWastepayApproved,
  initialValues = {},
  clients,
  invoicesExporting = false,
  invoiceProvider,
  handleExport
}) {
  function handleClear () {
    setFormValues({
      startDate: null,
      endDate: null,
      accountName: null,
      invoiceNumber: null,
      status: ''
    })
    onClear({
      accountName: null,
      invoiceNumber: null,
      status: null,
      startDate: null,
      endDate: null
    })
  }

  useEffect(() => {
    setFormValues(initialValues)
  }, [initialValues])

  function handleOnChange (updates) {
    setFormValues(prevValues => ({ ...prevValues, ...updates }))
  }

  function handleSubmit (event) {
    event.preventDefault()
    onSubmit({
      ...formValues,
      status: formValues.status && formValues.status.length ? formValues.status : null
    })
  }

  function formatDate (date) {
    const dateSplit = (date).split('-')
    const dateYear = parseInt(dateSplit[0])
    const dateMonth = parseInt(dateSplit[1] - 1)
    const dateDay = parseInt(dateSplit[2])
    return new Date(dateYear, dateMonth, dateDay).toUTCString()
  }
  const [formValues, setFormValues] = useState(initialValues)

  function handleFromDateChange (value) {
    const formattedDate = value ? dayjs(value).format('YYYY-MM-DD') : undefined
    handleOnChange({ startDate: formattedDate })
  }
  function handleToDateChange (value) {
    const formattedDate = value ? dayjs(value).format('YYYY-MM-DD') : undefined
    if (value) {
      handleOnChange({
        endDate: formattedDate
      })
    }
  }

  return (
    <div className={styles.searchContainer}>
      <div className={cn(styles.panel)}>
        <form onSubmit={handleSubmit} className={cn(styles.form)}>
          <div className={styles.inputs}>
            <label className={`${styles.label} ${styles.border}`}>
              Invoice Number
              <input
                type='text'
                className={cn(styles.input, 'form-control')}
                value={formValues.invoiceNumber || ''}
                onChange={(e) => handleOnChange({ invoiceNumber: e.target.value })}
              />
            </label>
            <label className={`${styles.label} ${styles.border}`} htmlFor='autocomplete_invoices_client_id'>
              Account Name
              <Autocomplete
                name='client_id'
                data={clients}
                value={formValues.accountName}
                id='autocomplete_invoices_client_id'
                onSelect={(value) => {
                  handleOnChange({ accountName: value ? value.name : null })
                }}
              />
            </label>
            <label className={`${styles.label} ${styles.border}`}>
              <div>Status</div>
              <select
                type='select'
                className={cn(styles.input, 'form-control')}
                value={formValues.status}
                onChange={(e) => handleOnChange({ status: e.currentTarget.value })}>
                <option key='placeholder' value=''></option>
                {Object.keys(INVOICE_STATUSES)?.filter((item) => invoiceProvider.toUpperCase() !== 'DISPATCHER' ? item !== INVOICE_STATUSES.OVERDUE : true).map((opt) =>
                  (
                    <option
                      value={opt}
                      key={opt}>
                      {opt === 'CLOSED_IN_QBO' ? 'Closed in QBO' : enumToTitleCase(opt)}
                    </option>
                  )
                )}
              </select>
            </label>
            <label className={`${styles.label} control-label`} htmlFor='created_from'>
              Start Date
              <DatePicker
                dateFormat='MM/dd/yyyy'
                selected={formValues.startDate ? new Date(formatDate(formValues.startDate)) : undefined}
                onChange={(val) => handleFromDateChange(val)}
                maxDate={formValues.endDate ? new Date(formatDate(formValues.endDate)) : undefined}
                fixedHeight
              />
            </label>
            <label className={`${styles.label} control-label`} htmlFor='created_to'>
              End Date
              <DatePicker
                name='endDate'
                dateFormat='MM/dd/yyyy'
                selected={formValues.endDate ? new Date(formatDate(formValues.endDate)) : undefined}
                onChange={(val) => handleToDateChange(val)}
                minDate={formValues.startDate ? new Date(formatDate(formValues.startDate)) : undefined}
                fixedHeight
              />
            </label>
          </div>
          <div className={styles.btns}>
            <div>
              <button
                type='button'
                tabIndex={-1}
                onClick={handleExport}
                className='btn btn-primary-dk'>
                {invoicesExporting
                  ? (<Spinner name='circle' className='mx-auto' color='white' fadeIn='none' />)
                  : (
                    <>
                      <span className='pull-left'>Export</span>
                      <i className='material-icons pull-right space-left'>get_app</i>
                    </>)}
              </button>
            </div>
            <div>
              <button
                type='submit'
                className='dis-btn dis-btn-sm dis-btn-success'
                disabled={isFetching}>
                Search
                <i className='material-icons dis-btn-icon'>search</i>
              </button>
              <button
                type='button'
                onClick={handleClear}
                className={cn(styles.clearBtn, 'dis-btn dis-btn-white dis-btn-sm')}
                disabled={isFetching}>
                Clear
                <i className='material-icons dis-btn-icon'>clear</i>
              </button>
            </div>
          </div>
        </form>
      </div>
      {isWastepayEnabled && !isWastepayApproved && <NotWastepayApproved />}
    </div>
  )
}

InvoicesSearchForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  isFetching: PropTypes.bool,
  isWastepayEnabled: PropTypes.bool.isRequired,
  isWastepayApproved: PropTypes.bool.isRequired,
  initialValues: PropTypes.shape({
    qboDocNumber: PropTypes.string,
    minTickets: PropTypes.number,
    clientName: PropTypes.string
  }),
  clients: PropTypes.array.isRequired,
  invoicesExporting: PropTypes.bool,
  invoiceProvider: PropTypes.string.isRequired,
  handleExport: PropTypes.func.isRequired
}

function NotWastepayApproved () {
  return (
    <div className={styles.wpad}>
      <h6>NEW!</h6>
      <h2 className={styles.ad}>Credit Card Processing</h2>
      <p>With the new WastePay integration, charge a credit card from a ticket!</p>
      <a href='https://www.dispatcher.com/payments-demo' target='_blank' rel='noopener nofollow noreferrer'>Learn More</a>
    </div>
  )
}
